import { ModalCloseButton, ModalShell } from '@busy/ui';
import type { DayBucket } from '@busy/api';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';

// Popover triggered by tapping a bar in the revenue chart. Shows the day's
// revenue + visits + avg ticket plus deltas vs the range average so the barber
// can spot strong / weak days at a glance.
export function DayHighlightsPopover({
  day,
  rangeAvgRevenueCents,
  rangeAvgVisits,
  onClose,
}: {
  day: DayBucket;
  rangeAvgRevenueCents: number;
  rangeAvgVisits: number;
  onClose: () => void;
}) {
  const t = useT();
  const language = useUI((s) => s.language);
  const date = new Date(day.ms);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isToday = date.getTime() === today.getTime();

  const dateLabel = date.toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const revPct =
    rangeAvgRevenueCents > 0
      ? Math.round(((day.revenue_cents - rangeAvgRevenueCents) / rangeAvgRevenueCents) * 100)
      : 0;
  const visitsPct =
    rangeAvgVisits > 0
      ? Math.round(((day.visits - rangeAvgVisits) / rangeAvgVisits) * 100)
      : 0;

  return (
    <ModalShell
      open
      onClose={onClose}
      size="sm"
      ariaLabel={t('dash.day.aria', { date: dateLabel })}
    >
      <ModalCloseButton onClose={onClose} />
      <div className="p-5 pr-12">
        <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-ink-500">
          {isToday ? t('dash.day.todayEyebrow') : t('dash.day.eyebrow')}
        </div>
        <h2 className="font-display text-2xl mt-0.5">{dateLabel}</h2>
      </div>

      <div className="px-5 pb-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Stat
            label={t('dash.day.stat.revenue')}
            value={`$${(day.revenue_cents / 100).toFixed(0)}`}
            delta={day.visits > 0 ? formatPct(revPct) : null}
            deltaTone={revPct >= 0 ? 'up' : 'down'}
          />
          <Stat
            label={t('dash.day.stat.cuts')}
            value={day.visits.toString()}
            delta={day.visits > 0 ? formatPct(visitsPct) : null}
            deltaTone={visitsPct >= 0 ? 'up' : 'down'}
          />
          <Stat
            label={t('dash.day.stat.avgTicket')}
            value={`$${(day.avg_ticket_cents / 100).toFixed(0)}`}
            delta={null}
          />
          {/* Tips stat — replaces the prior "Daily rev avg" benchmark.
              The benchmark was reachable by looking at the chart's
              shape; the tip dollar amount + day-level rate % for THIS
              day is brand new info only this popover surfaces. */}
          <Stat
            label={t('dash.day.stat.tips')}
            value={`$${(day.tip_cents / 100).toFixed(0)}`}
            delta={null}
            sub={(() => {
              if (day.visits === 0) return undefined;
              const avgDollars = day.tip_cents / day.visits / 100;
              const avgStr = `$${avgDollars.toFixed(avgDollars % 1 === 0 ? 0 : 2)}`;
              const ratePct =
                day.revenue_cents > 0
                  ? Math.round((day.tip_cents / day.revenue_cents) * 100)
                  : null;
              if (ratePct != null && ratePct > 0) {
                return t('dash.day.tipsAvgWithRate', { avg: avgStr, pct: ratePct });
              }
              return t('dash.day.tipsAvgPerCut', { avg: avgStr });
            })()}
          />
        </div>
        {/* Total earned — sum of cuts + tips. Lives outside the grid so
            it reads as the bottom-line takeaway, not a fifth stat. */}
        {day.visits > 0 && (
          <div className="flex items-center justify-between border-t border-line pt-3">
            <span className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
              {t('dash.day.totalEarned')}
            </span>
            <span className="font-display text-2xl text-navy-900 dark:text-gold-500 tabular-nums">
              ${((day.revenue_cents + day.tip_cents) / 100).toFixed(0)}
            </span>
          </div>
        )}
        {day.visits === 0 && (
          <p className="text-xs text-ink-500 text-center pt-1">{t('dash.day.empty')}</p>
        )}
      </div>
    </ModalShell>
  );
}

function Stat({
  label,
  value,
  delta,
  deltaTone,
  sub,
}: {
  label: string;
  value: string;
  delta: string | null;
  deltaTone?: 'up' | 'down';
  sub?: string;
}) {
  const t = useT();
  return (
    <div className="rounded-xl border border-line bg-bg-alt p-3">
      <div className="text-[10px] uppercase tracking-wide text-ink-500">{label}</div>
      <div className="font-display text-2xl tabular-nums mt-0.5 text-ink-900">{value}</div>
      {sub && <div className="text-[10px] text-ink-500 mt-0.5">{sub}</div>}
      {delta && (
        <div
          className={`text-[11px] font-bold mt-1 tabular-nums ${
            deltaTone === 'up' ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {t('dash.day.vsAvg', { delta })}
        </div>
      )}
    </div>
  );
}

function formatPct(n: number): string {
  if (n === 0) return '±0%';
  return `${n > 0 ? '+' : ''}${n}%`;
}
